import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faCheckCircle, faCircle, faClock } from '@fortawesome/free-solid-svg-icons';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import { APIRoutes } from '@/services/api.routes';
import type { Video } from '@/models';

interface VideoListCardProps {
  video: Video;
  videoNumber: number;
  isWatched: boolean;
  courseId: string;
  moduleId: string;
}

const VideoListCard: React.FC<VideoListCardProps> = ({
  video,
  videoNumber,
  isWatched,
  courseId,
  moduleId
}) => {
  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return 'Unbekannt';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Link
      to={`/courses/${courseId}/modules/${moduleId}/videos/${video.id}`}
      className="block bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 overflow-hidden group"
    >
      <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6 md:gap-8">
        {/* Status Icon - immer sichtbar */}
        <div className="flex-shrink-0 self-center">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
            isWatched
              ? 'bg-green-100 dark:bg-green-900'
              : 'bg-gray-100 dark:bg-gray-700'
          }`}>
            <FontAwesomeIcon
              icon={isWatched ? faCheckCircle : faCircle}
              className={`w-5 h-5 sm:w-6 sm:h-6 ${
                isWatched
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            />
          </div>
        </div>

        {/* Thumbnail - kleiner auf Mobile, größer auf Desktop */}
        {video.thumbnail && (
          <div className="flex-shrink-0 w-20 sm:w-32 md:w-40 lg:w-48 aspect-video self-center">
            <img
              src={APIRoutes.getBlobContentUrl(video.thumbnail)}
              alt={video.title}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {videoNumber}. {video.title}
              </h3>
              <div className="flex items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faClock} className="w-3 h-3 sm:w-4 sm:h-4" />
                  {formatDuration(video.duration)}
                </span>
                {isWatched && (
                  <span className="text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                    <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
                    <span className="hidden sm:inline">Abgeschlossen</span>
                  </span>
                )}
              </div>
              {video.description && (
                <div className="mt-2 line-clamp-1 sm:line-clamp-2 md:line-clamp-3">
                  <MarkdownRenderer className="text-xs sm:text-sm">
                    {video.description}
                  </MarkdownRenderer>
                </div>
              )}
            </div>

            {/* Play Icon - immer sichtbar, aber kleiner auf mobil */}
            <div className="flex-shrink-0 self-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-800 transition-colors">
                <FontAwesomeIcon icon={faPlay} className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 ml-0.5 sm:ml-1" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default VideoListCard;
